/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on
 * an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import React from 'react';
import { styled, t, supersetTheme } from '@superset-ui/core';
import { InfoTooltipWithTrigger } from '@superset-ui/chart-controls';
import { Form, FormItem } from 'src/components/Form';
import Select from 'src/components/Select/Select';
import { Col, Row } from 'src/components';
import { Input, InputNumber } from 'src/components/Input';
import Button from 'src/components/Button';
import { LinkFormattingRule, LINK_TYPE, OBJECT_FIT } from './types';

const FullWidthInput = styled(Input)`
  width: 100%;
`;

const JustifyEnd = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const THEME_COLOR = supersetTheme.colors.primary.base;

// 超链接 SVG 图标
const LinkIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke={THEME_COLOR}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ marginRight: 6, verticalAlign: 'middle' }}
  >
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

// 图片 SVG 图标
const ImageIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke={THEME_COLOR}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ marginRight: 6, verticalAlign: 'middle' }}
  >
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
);

const linkTypeOptions = [
  {
    value: LINK_TYPE.LINK,
    label: (
      <span>
        <LinkIcon />
        {t('超链接')}
      </span>
    ),
  },
  {
    value: LINK_TYPE.IMAGE,
    label: (
      <span>
        <ImageIcon />
        {t('图片')}
      </span>
    ),
  },
];

// 图片覆盖形式选项
const objectFitOptions = [
  { value: OBJECT_FIT.COVER, label: t('覆盖填充 (cover)') },
  { value: OBJECT_FIT.CONTAIN, label: t('自适应 (contain)') },
  { value: OBJECT_FIT.FILL, label: t('拉伸填充 (fill)') },
  { value: OBJECT_FIT.NONE, label: t('原始大小 (none)') },
  { value: OBJECT_FIT.SCALE_DOWN, label: t('缩小适应 (scale-down)') },
];

const rulesRequired = [{ required: true, message: t('Required') }];

// 默认图片尺寸
const DEFAULT_IMAGE_WIDTH = 100;
const DEFAULT_IMAGE_HEIGHT = 100;
const DEFAULT_BORDER_RADIUS = 0;
const DEFAULT_OBJECT_FIT = OBJECT_FIT.COVER;

export const LinkFormattingPopoverContent = ({
  config,
  onChange,
  columns = [],
}: {
  config?: LinkFormattingRule;
  onChange: (config: LinkFormattingRule) => void;
  columns: { label: string; value: string }[];
}) => {
  const handleFormFinish = (values: LinkFormattingRule) => {
    const newValues = { ...values };

    // 如果是图片类型，确保有默认值
    if (newValues.linkType === LINK_TYPE.IMAGE) {
      newValues.imageWidth = newValues.imageWidth ?? DEFAULT_IMAGE_WIDTH;
      newValues.imageHeight = newValues.imageHeight ?? DEFAULT_IMAGE_HEIGHT;
      newValues.imageBorderRadius =
        newValues.imageBorderRadius ?? DEFAULT_BORDER_RADIUS;
      newValues.objectFit = newValues.objectFit ?? DEFAULT_OBJECT_FIT;
    }

    onChange(newValues);
  };

  return (
    <Form
      onFinish={handleFormFinish}
      initialValues={{
        linkType: LINK_TYPE.LINK,
        imageWidth: DEFAULT_IMAGE_WIDTH,
        imageHeight: DEFAULT_IMAGE_HEIGHT,
        imageBorderRadius: DEFAULT_BORDER_RADIUS,
        objectFit: DEFAULT_OBJECT_FIT,
        ...config,
      }}
      requiredMark={false}
      layout="vertical"
    >
      <Row gutter={12}>
        <Col span={12}>
          <FormItem name="column" label={t('选择列')} rules={rulesRequired}>
            <Select ariaLabel={t('选择列')} options={columns} />
          </FormItem>
        </Col>
        <Col span={12}>
          <FormItem name="linkType" label={t('展现形式')} rules={rulesRequired}>
            <Select ariaLabel={t('展现形式')} options={linkTypeOptions} />
          </FormItem>
        </Col>
      </Row>

      <FormItem
        noStyle
        shouldUpdate={(prev, cur) => prev.linkType !== cur.linkType}
      >
        {({ getFieldValue }) => {
          const linkType = getFieldValue('linkType');

          if (linkType === LINK_TYPE.LINK) {
            return (
              <Row gutter={12}>
                <Col span={24}>
                  <FormItem
                    name="displayText"
                    label={
                      <span>
                        {t('显示文本')}{' '}
                        <InfoTooltipWithTrigger
                          tooltip={t('链接显示的文本内容，如"查看详情"')}
                        />
                      </span>
                    }
                    rules={rulesRequired}
                  >
                    <FullWidthInput
                      placeholder={t('请输入显示文本，如：查看详情')}
                    />
                  </FormItem>
                </Col>
              </Row>
            );
          }

          if (linkType === LINK_TYPE.IMAGE) {
            return (
              <>
                <Row gutter={12}>
                  <Col span={12}>
                    <FormItem
                      name="imageWidth"
                      label={
                        <span>
                          {t('宽度 (px)')}{' '}
                          <InfoTooltipWithTrigger tooltip={t('图片显示宽度')} />
                        </span>
                      }
                      rules={rulesRequired}
                    >
                      <InputNumber
                        min={1}
                        max={1000}
                        style={{ width: '100%' }}
                        placeholder="100"
                      />
                    </FormItem>
                  </Col>
                  <Col span={12}>
                    <FormItem
                      name="imageHeight"
                      label={
                        <span>
                          {t('高度 (px)')}{' '}
                          <InfoTooltipWithTrigger tooltip={t('图片显示高度')} />
                        </span>
                      }
                      rules={rulesRequired}
                    >
                      <InputNumber
                        min={1}
                        max={1000}
                        style={{ width: '100%' }}
                        placeholder="100"
                      />
                    </FormItem>
                  </Col>
                </Row>
                <Row gutter={12}>
                  <Col span={12}>
                    <FormItem
                      name="imageBorderRadius"
                      label={
                        <span>
                          {t('圆角 (px)')}{' '}
                          <InfoTooltipWithTrigger
                            tooltip={t('图片圆角大小，0为直角')}
                          />
                        </span>
                      }
                    >
                      <InputNumber
                        min={0}
                        max={500}
                        style={{ width: '100%' }}
                        placeholder="0"
                      />
                    </FormItem>
                  </Col>
                  <Col span={12}>
                    <FormItem
                      name="objectFit"
                      label={
                        <span>
                          {t('覆盖形式')}{' '}
                          <InfoTooltipWithTrigger
                            tooltip={t(
                              '图片在容器内的显示方式：覆盖填充会裁剪图片以填满容器，自适应会保持比例完整显示',
                            )}
                          />
                        </span>
                      }
                    >
                      <Select
                        ariaLabel={t('覆盖形式')}
                        options={objectFitOptions}
                      />
                    </FormItem>
                  </Col>
                </Row>
              </>
            );
          }

          return null;
        }}
      </FormItem>

      <FormItem>
        <JustifyEnd>
          <Button htmlType="submit" buttonStyle="primary">
            {t('Apply')}
          </Button>
        </JustifyEnd>
      </FormItem>
    </Form>
  );
};
